import { supabase } from '../../lib/supabase';
import type { PostmarkIdentity } from './types';

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('postmark-signatures', {
    body,
  });
  if (error) {
    let detail: string | null = null;
    const ctx = (error as { context?: unknown }).context;
    if (ctx && typeof (ctx as Response).json === 'function') {
      try {
        const parsed = await (ctx as Response).clone().json();
        if (parsed && typeof parsed.error === 'string') detail = parsed.error;
      } catch {
        try {
          detail = await (ctx as Response).clone().text();
        } catch {
          /* ignore */
        }
      }
    }
    if (!detail) {
      detail = (data as { error?: string } | null)?.error ?? null;
    }
    throw new Error(detail || error.message);
  }
  return data as T;
}

export async function listPostmarkIdentities(): Promise<{
  identities: PostmarkIdentity[];
  signatureCount: number;
  domainCount: number;
}> {
  const data = await invoke<{
    identities?: PostmarkIdentity[];
    signatures?: Array<{
      id: number;
      name: string;
      emailAddress: string;
      replyToEmailAddress: string | null;
      domain: string | null;
      confirmed: boolean;
      fromLine: string;
    }>;
    signatureCount?: number;
    domainCount?: number;
    totalCount?: number;
  }>({ action: 'list' });

  if (data.identities && data.identities.length > 0) {
    return {
      identities: data.identities,
      signatureCount: data.signatureCount ?? 0,
      domainCount: data.domainCount ?? 0,
    };
  }

  // Backward compat if an older edge fn only returns `signatures`.
  const legacy = (data.signatures ?? []).map(
    (s): PostmarkIdentity => ({
      kind: 'signature',
      id: s.id,
      name: s.name,
      emailAddress: s.emailAddress,
      replyToEmailAddress: s.replyToEmailAddress,
      domain: s.domain,
      confirmed: s.confirmed,
      fromLine: s.fromLine,
    }),
  );

  return {
    identities: legacy,
    signatureCount: data.signatureCount ?? legacy.length,
    domainCount: data.domainCount ?? 0,
  };
}
